import { useMutation } from '@tanstack/react-query';
import { PermanentJobsService } from '../services';

/**
 * React Query mutation hook for creating or publishing a job posting
 *
 * @param {Object} [options] - Hook options
 * @param {(postingId: string, variables: import('../types/CreateJobPosting.parameters').ICreateJobPostingPayload) => void} [options.onSuccess] - Success callback, receives postingId and variables
 * @param {(error: Error) => void} [options.onError] - Error callback
 *
 * @returns {{
 *   mutate: (payload: import('../types/CreateJobPosting.parameters').ICreateJobPostingPayload) => void,
 *   mutateAsync: (payload: import('../types/CreateJobPosting.parameters').ICreateJobPostingPayload) => Promise<string>,
 *   data: string | undefined,
 *   error: Error | null,
 *   isLoading: boolean,
 *   isError: boolean,
 *   isSuccess: boolean,
 *   reset: () => void
 * }} Mutation object
 *
 * @example
 * // Creating a new job posting
 * const { mutate, isLoading } = useCreateOrPublishJobPosting({
 *   onSuccess: (postingId) => {
 *     console.log('Created posting:', postingId);
 *   }
 * });
 *
 * mutate({
 *   officeId: 'office-123',
 *   planId: 'plan-123',
 *   title: 'Dental Hygienist',
 *   professionType: 'RDH',
 *   // ... other required fields
 * });
 *
 * @example
 * // Publishing an existing draft
 * const { mutate } = useCreateOrPublishJobPosting();
 *
 * mutate({
 *   postingId: 'existing-posting-123',
 *   planId: 'plan-123',
 *   title: 'Dental Hygienist',
 *   // ... other fields
 * });
 */
export const useCreateOrPublishJobPosting = (options = {}) => {
  const { onSuccess, onError } = options;

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const postingId = await PermanentJobsService.createOrPublishJobPosting(payload);
      return postingId;
    },
    onSuccess: (postingId, variables) => {
      if (onSuccess) {
        onSuccess(postingId, variables);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return mutation;
};
